import React, { useState } from 'react';
import { View, Pressable } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { TAB_NAMES } from '~/client/components/ActionBar';
import TextInput from '~/client/components/TextInput';
import Caption from '~/client/components/Caption';
import Error from '~/client/components/Error';
import ActivityIndicator from '~/client/components/ActivityIndicator';
import Image from '~/client/components/Image';
import Text from '~/client/components/Text';
import { fetchFromServer } from '~/client/utils/fetchJSONFromServer';
import { RootStackParamList } from '~/client/NavigationConfig';
import { UrlTypes } from '~/client/utils/constants';
import navigateToResource from '~/client/utils/navigateToResource';

interface AppleMusicSongResult {
  isrc: string;
  name: string;
  artistName: string;
  albumName: string;
  artworkUrl: string;
}

interface AppleMusicAlbumResult {
  appleUrl: string;
  name: string;
  artistName: string;
  artworkUrl: string;
}

interface AppleMusicArtistResult {
  name: string;
}

interface AppleMusicSearchResults {
  songs: AppleMusicSongResult[];
  albums: AppleMusicAlbumResult[];
  artists: AppleMusicArtistResult[];
}

const EMPTY_RESULTS: AppleMusicSearchResults = {
  songs: [],
  albums: [],
  artists: [],
};

export default function SearchAppleMusic({ active }: { active: boolean }) {
  const navigation =
    useNavigation<
      NativeStackNavigationProp<
        RootStackParamList,
        'Song' | 'Album' | 'Playlist'
      >
    >();

  const [results, setResults] =
    useState<AppleMusicSearchResults>(EMPTY_RESULTS);
  const [isSearching, setIsSearching] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!active) {
    return null;
  }

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      return;
    }

    setIsSearching(true);
    setError(null);
    try {
      const response = await fetchFromServer(
        `/search/apple/${encodeURIComponent(query.trim())}`,
      );

      if (!response.ok) {
        setError('Search failed. Please try again.');
        return;
      }

      const responseBody: AppleMusicSearchResults = await response.json();

      setResults(responseBody);
      if (
        responseBody.songs.length === 0 &&
        responseBody.albums.length === 0 &&
        responseBody.artists.length === 0
      ) {
        setError('No results found.');
      }
    } catch (e) {
      setError('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectSong = async (isrc: string) => {
    setIsResolving(true);
    setError(null);
    try {
      const response = await fetchFromServer(
        `/song/fromIsrc/${encodeURIComponent(isrc)}`,
      );

      if (!response.ok) {
        setError('Could not add this song. Please try again.');
        return;
      }

      const { resourceId } = await response.json();

      setResults(EMPTY_RESULTS);
      navigation.push('Song', { resourceId: String(resourceId) });
    } catch (e) {
      setError('Could not add this song. Please try again.');
    } finally {
      setIsResolving(false);
    }
  };

  // Albums go through the same URL-submission pipeline SubmitUrl uses, since
  // importing an album (and all of its tracks) is async — the server may
  // need to redirect to an already-known album, or park us on the Waiting
  // screen until it's done creating a new one.
  const handleSelectAlbum = async (appleUrl: string) => {
    setIsResolving(true);
    setError(null);
    try {
      const response = await fetchFromServer(`/s?s=${appleUrl}`);

      if (!response.ok) {
        setError('Could not add this album. Please try again.');
        return;
      }

      const responseData: {
        url?: string;
        resourceId?: string;
        resourceSourceUrl?: string;
        type: UrlTypes;
      } = await response.json();

      setResults(EMPTY_RESULTS);

      if (responseData.url && responseData.resourceId) {
        navigateToResource({
          navigation,
          resourceType: responseData.type,
          resourceId: responseData.resourceId,
        });
      } else {
        navigation.navigate('Waiting', {
          resourceSourceUrl: responseData.resourceSourceUrl as string,
        });
      }
    } catch (e) {
      setError('Could not add this album. Please try again.');
    } finally {
      setIsResolving(false);
    }
  };

  return (
    <View className="my-4 w-[93%] md:w-full">
      <Error errorText={error} />
      <TextInput
        // @ts-expect-error
        onSubmitEditing={(event) =>
          handleSearch(event.nativeEvent.text ?? '')
        }
        id={TAB_NAMES.SEARCH_APPLE_MUSIC}
        type="text"
        name="appleMusicSearch"
        placeholder="e.g. Bohemian Rhapsody"
      />
      <Caption>Search Apple Music's catalog and add a result.</Caption>

      {isSearching || isResolving ? <ActivityIndicator /> : null}

      {results.songs.length > 0 ? (
        <View style={{ marginBottom: '1rem' }}>
          <Text style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
            Songs
          </Text>
          {results.songs.map((result) => (
            <Pressable
              key={result.isrc}
              onPress={() => handleSelectSong(result.isrc)}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                marginBottom: '0.75rem',
                // @ts-expect-error `cursor` is a web-only style property
                cursor: 'pointer',
              }}
            >
              <Image
                src={result.artworkUrl
                  ?.replace('{w}', '40')
                  .replace('{h}', '40')}
                style={{ width: 40, height: 40, marginRight: '1em' }}
              />
              <View>
                <Text style={{ fontWeight: 'bold' }}>{result.name}</Text>
                <Text>
                  {result.artistName} — {result.albumName}
                </Text>
              </View>
            </Pressable>
          ))}
        </View>
      ) : null}

      {results.albums.length > 0 ? (
        <View style={{ marginBottom: '1rem' }}>
          <Text style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
            Albums
          </Text>
          {results.albums.map((result) => (
            <Pressable
              key={result.appleUrl}
              onPress={() => handleSelectAlbum(result.appleUrl)}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                marginBottom: '0.75rem',
                // @ts-expect-error `cursor` is a web-only style property
                cursor: 'pointer',
              }}
            >
              <Image
                src={result.artworkUrl
                  ?.replace('{w}', '40')
                  .replace('{h}', '40')}
                style={{ width: 40, height: 40, marginRight: '1em' }}
              />
              <View>
                <Text style={{ fontWeight: 'bold' }}>{result.name}</Text>
                <Text>{result.artistName}</Text>
              </View>
            </Pressable>
          ))}
        </View>
      ) : null}

      {results.artists.length > 0 ? (
        <View>
          <Text style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
            Artists
          </Text>
          {results.artists.map((result) => (
            <Pressable
              key={result.name}
              onPress={() => handleSearch(result.name)}
              style={{
                marginBottom: '0.75rem',
                // @ts-expect-error `cursor` is a web-only style property
                cursor: 'pointer',
              }}
            >
              <Text>{result.name}</Text>
            </Pressable>
          ))}
        </View>
      ) : null}
    </View>
  );
}
